import { CheckCircle, XCircle, Loader2 } from 'lucide-react'
import { Input, Label } from '@/components/ui'
import { WizardStep, WizardActions } from './WizardStep'
import { cn } from '@/lib/utils'

interface EmployerCodeStepProps {
  employerCode: string
  onEmployerCodeChange: (code: string) => void
  onNext: () => void
  onBack: () => void
  onSkip: () => void
  isValidating?: boolean
  validationResult?: { valid: boolean; message?: string; employerName?: string } | null
}

export function EmployerCodeStep({
  employerCode,
  onEmployerCodeChange,
  onNext,
  onBack,
  onSkip,
  isValidating = false,
  validationResult,
}: EmployerCodeStepProps) {
  const hasCode = employerCode.trim().length > 0
  const isValid = validationResult?.valid === true

  return (
    <WizardStep
      title="Have an employer code?"
      description="If your workplace offers FamSteady, pop in your code to unlock the good stuff"
    >
      <div className="space-y-4">
        <div>
          <Label htmlFor="employerCode">Employer Code</Label>
          <div className="relative mt-1">
            <Input
              id="employerCode"
              type="text"
              placeholder="e.g., ACME-2024"
              value={employerCode}
              onChange={(e) => onEmployerCodeChange(e.target.value.toUpperCase())}
              className={cn(
                'pr-10',
                validationResult && !validationResult.valid && 'border-error-500'
              )}
            />
            {isValidating && (
              <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              </div>
            )}
            {!isValidating && validationResult && (
              <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                {validationResult.valid ? (
                  <CheckCircle className="h-4 w-4 text-success-500" />
                ) : (
                  <XCircle className="h-4 w-4 text-error-500" />
                )}
              </div>
            )}
          </div>

          {validationResult && (
            <p
              className={cn(
                'mt-1 text-xs',
                validationResult.valid ? 'text-success-600' : 'text-error-600'
              )}
            >
              {validationResult.valid
                ? `Valid code from ${validationResult.employerName}`
                : validationResult.message || 'Invalid employer code'}
            </p>
          )}

          {!hasCode && (
            <p className="mt-1 text-xs text-gray-500">
              No code? No worries — just skip ahead!
            </p>
          )}
        </div>
      </div>

      <WizardActions
        onBack={onBack}
        onNext={onNext}
        onSkip={onSkip}
        nextLabel={hasCode ? 'Apply Code' : 'Continue'}
        isNextDisabled={hasCode && !isValid}
        isNextLoading={isValidating}
        showSkip={hasCode}
      />
    </WizardStep>
  )
}
